// LUCKY DUCKY — Premium V1 audio pack.
//
// Every entry maps a game-code event → a licensed WAV file that ships from
// /public/games/lucky-ducky/audio/.  Event names not present in this pack
// are silently no-op'd by AudioManager (that is how the previous synth
// beep noises get REMOVED — we simply do not declare them here).
//
// The canonical event → file mapping is also duplicated in
// /public/games/lucky-ducky/audio/LUCKY_DUCKY_AUDIO_EVENT_MAP.json so
// operations can audit which WAV corresponds to which game event.
//
// IMPORTANT
// - Reel stops all use ONE source (LD_AUDIO_REEL_STOP_SOURCE.wav) and
//   vary only via `playbackRate` + `volume` — no separate stop files.
// - Background music and BETO feature music are looped through the same
//   AudioManager.loop() codepath — the manager guarantees a single loop
//   per event, so multiple presses can never stack.

const A = '/games/lucky-ducky/audio';

export const LUCKY_DUCKY_PREMIUM_PACK = {
  // ── Ambient bed ─────────────────────────────────────────────────────
  // Base game background music — low, non-fatiguing.  Started ONCE on
  // the first user gesture (mobile autoplay unlock) and looped from
  // there.  Ducked/paused during BETO RESPINS.
  background_music: {
    src: `${A}/LD_AUDIO_BG_CLASSIC_FRUIT.wav`,
    loop: true, volume: 0.18,
  },
  game_intro: { src: `${A}/LD_AUDIO_GAME_INTRO.wav`, volume: 0.55 },

  // ── Spin lifecycle ─────────────────────────────────────────────────
  // NORMAL reel-spin sequence (licensed GfxSounds pack):
  //   SPIN pressed → spin_start (once) → reel_spin_loop (subtle, single
  //   instance) → one mini-pull stop cue synchronized to each of the five
  //   ACTUAL visual reel-stops → loop fades/stops on the last reel.
  // No beeps, no per-symbol ticking — the loop carries the motion and the
  // five stop cues carry the rhythm.
  spin_start:     { src: `${A}/LD_REEL_START.wav`, volume: 0.5 },
  reel_spin_loop: { src: `${A}/LD_REEL_LOOP_NEW.wav`, loop: true, volume: 0.16 },

  // Reel-stop cues — ONE clean licensed source ("mini pull 3"), varied only
  // by barely-noticeable per-reel pitch + gain so five landings never sound
  // like the same sample machine-gunned.  Fired by the real onfinish land
  // events in LuckyDucky.js (NOT a guessing timer).
  reel_stop_1: { src: `${A}/LD_REEL_STOP.wav`, playbackRate: 0.98, volume: 0.90, poolSize: 2 },
  reel_stop_2: { src: `${A}/LD_REEL_STOP.wav`, playbackRate: 0.99, volume: 0.92, poolSize: 2 },
  reel_stop_3: { src: `${A}/LD_REEL_STOP.wav`, playbackRate: 1.00, volume: 0.94, poolSize: 2 },
  reel_stop_4: { src: `${A}/LD_REEL_STOP.wav`, playbackRate: 1.01, volume: 0.96, poolSize: 2 },
  reel_stop_5: { src: `${A}/LD_REEL_STOP.wav`, playbackRate: 1.02, volume: 0.98, poolSize: 2 },

  // ── Win tiers (ratio-based) ────────────────────────────────────────
  // AudioManager.playWinBySize routes to win_small/medium/big/mega
  // based on WIN / BET thresholds (1×/3×/10×/25×).
  win_small:   { src: `${A}/LD_AUDIO_SMALL_WIN.wav`,  volume: 0.70 },
  win_medium:  { src: `${A}/LD_AUDIO_MEDIUM_WIN.wav`, volume: 0.75 },
  win_big:     { src: `${A}/LD_AUDIO_BIG_WIN.wav`,    volume: 0.80 },
  win_mega:    { src: `${A}/LD_AUDIO_BIG_REWARD.wav`, volume: 0.85 },
  win_countup: { src: `${A}/LD_AUDIO_COIN_CASCADE.wav`, volume: 0.55 },

  // ── Multiplier / DOUBLE WILD ───────────────────────────────────────
  feature_multiplier:   { src: `${A}/LD_AUDIO_MULTIPLIER.wav`, volume: 0.75 },
  multiplier_x3_reveal: { src: `${A}/LD_AUDIO_MULTIPLIER.wav`, volume: 0.78 },

  // ── Prize reveal (authoritative reveal moments only) ───────────────
  prize_reveal: { src: `${A}/LD_AUDIO_PRIZE_REVEAL.wav`, volume: 0.75 },

  // ── BETO RESPINS ───────────────────────────────────────────────────
  // Feature-ENTRY cue.  The old ceremonial horn (LD_AUDIO_BETO_INTRO.wav)
  // was REMOVED from active gameplay per product-owner request — it did
  // not fit Lucky Ducky.  Entry now uses a short, bright, playful reveal
  // that leads straight into the feature theme.
  beto_respins_enter: { src: `${A}/LD_AUDIO_PRIZE_REVEAL.wav`, volume: 0.8 },
  // Feature music bed — fast/upbeat energetic loop (ALT track), looped for
  // the whole feature and NEVER restarted per spin.
  beto_music_start:   { src: `${A}/LD_AUDIO_BETO_FEATURE.wav`, loop: true, volume: 0.32, playbackRate: 1.22 },
  beto_music_loop:    { src: `${A}/LD_AUDIO_BETO_FEATURE.wav`, loop: true, volume: 0.32, playbackRate: 1.22 },
  // BONUS HIT — one short, bright impact each time a BONUS symbol lands.
  // Runtime pitch/gain variation (A/B/C) is applied at the call site so
  // repeats never sound identical while staying the same sound family.
  bonus_symbol_land:  { src: `${A}/LD_AUDIO_MULTIPLIER.wav`, volume: 0.52, maxDurationMs: 380, poolSize: 6 },
  // Feature complete stinger.
  beto_music_end:     { src: `${A}/LD_AUDIO_BETO_END.wav`, volume: 0.75 },
  feature_complete:   { src: `${A}/LD_AUDIO_BETO_END.wav`, volume: 0.75 },

  // Feature reel activity — spin-start whisper only, reel-stop cues
  // intentionally omitted (silent) so the feature bed remains clean.
  feature_spin_start: { src: `${A}/LD_AUDIO_SPIN_START.wav`, volume: 0.14 },
  free_play_spin:     { src: `${A}/LD_AUDIO_SPIN_START.wav`, volume: 0.12 },
  //   feature_reel_stop → silent

  // Feature win echoes.
  feature_small_win:  { src: `${A}/LD_AUDIO_SMALL_WIN.wav`,  volume: 0.68 },
  feature_medium_win: { src: `${A}/LD_AUDIO_MEDIUM_WIN.wav`, volume: 0.75 },
  feature_big_win:    { src: `${A}/LD_AUDIO_BIG_WIN.wav`,    volume: 0.80 },
  feature_total_win:  { src: `${A}/LD_AUDIO_BIG_REWARD.wav`, volume: 0.82 },

  // ── Deliberately UNDEFINED events (silent by design) ───────────────
  // These names still appear in LuckyDucky.js call sites but the guide
  // requires no beep/synth for them.  Leaving them undefined causes
  // AudioManager.play() to return early — no fallback, no noise.
  //
  //   ui_button, bet_change,
  //   bonus_tease, bonus_trigger, bonus_symbol_land,
  //   bonus_anticipation_start, bonus_anticipation_rise, bonus_final_reel_suspense,
  //   feature_retrigger, feature_extra_spins_awarded, beto_retrigger,
  //   beto_music_intensity_up, beto_music_final_spins, feature_final_spin,
  //   free_plays_trigger, beto_respin
};
